import { useCallback } from 'react';
import { Edge, Node } from '@xyflow/react';

import { ForLoopConnectionValidator } from '../services/ForLoopConnectionValidator';
import {
  ConnectionValidationError,
  ConnectionValidationResult,
  INodeInputConfiguration,
  INodeOutputConfiguration,
  NodeTypeCategory,
} from '../types/connections';
import { NodeConfig } from '../types/node-config';
import { INodeTypeDescription } from '../types/node-interface';

import { useWorkflow } from './useWorkflow';

export function useConnectionValidation(nodes: Node[], edges: Edge[]) {
  const { getNodeConfig } = useWorkflow();

  const getNodeTypeByName = useCallback(
    (typeName: string): NodeConfig | null => {
      return getNodeConfig(typeName) || null;
    },
    [getNodeConfig]
  );

  const getOutputConfiguration = useCallback(
    (nodeDesc: INodeTypeDescription): INodeOutputConfiguration | null => {
      const output = nodeDesc.output;
      if (!output) return null;

      // For nodes with no output
      if (!output.type) return null;

      return output;
    },
    []
  );

  const getInputConfiguration = useCallback(
    (nodeDesc: INodeTypeDescription): INodeInputConfiguration | null => {
      const input = nodeDesc.input;
      if (!input) return null;

      // For trigger nodes with no input (maxConnections: 0)
      if (!input.type || input.maxConnections === 0) return null;

      return input;
    },
    []
  );

  const validateConnectionCounts = useCallback(
    (
      sourceNode: Node,
      targetNode: Node,
      sourceNodeDesc: INodeTypeDescription,
      targetNodeDesc: INodeTypeDescription
    ): ConnectionValidationResult => {
      // Check if target node is a trigger (triggers can't have inputs)
      if (targetNodeDesc.category === NodeTypeCategory.TRIGGER) {
        return {
          isValid: false,
          error: ConnectionValidationError.TRIGGER_AS_TARGET,
          suggestion: 'Trigger nodes cannot have input connections',
        };
      }

      // Check if there is already an edge between source and target
      const existingEdge = edges.find(
        edge => edge.source === sourceNode.id && edge.target === targetNode.id
      );

      if (existingEdge) {
        return {
          isValid: false,
          error: ConnectionValidationError.DUPLICATE_CONNECTION,
          suggestion: 'A connection between these two nodes already exists',
        };
      }

      // Get source output configuration
      const sourceOutput = getOutputConfiguration(sourceNodeDesc);
      if (sourceOutput && sourceOutput.maxConnections !== undefined) {
        const currentOutputConnections = edges.filter(edge => edge.source === sourceNode.id).length;

        if (currentOutputConnections >= sourceOutput.maxConnections) {
          return {
            isValid: false,
            error: ConnectionValidationError.MAX_CONNECTIONS_EXCEEDED,
            suggestion: `Source node output can only have ${sourceOutput.maxConnections} connection(s)`,
          };
        }
      }

      // Get target input configuration
      const targetInput = getInputConfiguration(targetNodeDesc);
      if (targetInput && targetInput.maxConnections !== undefined) {
        const currentInputConnections = edges.filter(edge => edge.target === targetNode.id).length;

        if (currentInputConnections >= targetInput.maxConnections) {
          return {
            isValid: false,
            error: ConnectionValidationError.MAX_CONNECTIONS_EXCEEDED,
            suggestion: `Target node input can only have ${targetInput.maxConnections} connection(s)`,
          };
        }
      }

      return { isValid: true };
    },
    [edges, getOutputConfiguration, getInputConfiguration]
  );

  const validateConnection = useCallback(
    (sourceNode: Node, targetNode: Node): ConnectionValidationResult => {
      // Check if this is a self-connection
      if (sourceNode.id === targetNode.id) {
        return {
          isValid: false,
          error: ConnectionValidationError.CIRCULAR_DEPENDENCY,
          suggestion: 'A node cannot connect to itself',
        };
      }
      // Check if this is a ForLoop-related connection
      const isForLoopRelated =
        sourceNode.type === 'for_loop' ||
        targetNode.type === 'for_loop' ||
        sourceNode.parentId ||
        targetNode.parentId;

      // Validate ForLoop connections first if applicable
      if (isForLoopRelated) {
        const forLoopValidation = ForLoopConnectionValidator.validateConnection(
          sourceNode.id,
          targetNode.id,
          nodes,
          edges
        );

        if (!forLoopValidation.isValid) {
          return forLoopValidation;
        }
      }

      const sourceNodeType = getNodeTypeByName(sourceNode.type || '');
      const targetNodeType = getNodeTypeByName(targetNode.type || '');

      if (!sourceNodeType || !targetNodeType) {
        return {
          isValid: false,
          error: 'Unknown node type',
          suggestion: 'Ensure both nodes are properly registered',
        };
      }

      // Validate target node allows source node type (unidirectional validation)
      const targetInput = getInputConfiguration(targetNodeType.description);
      if (targetInput?.filter?.allowedNodeTypes) {
        const allowedTypeConfig = targetInput.filter.allowedNodeTypes.find(
          config => config.type === sourceNode.type
        );

        if (!allowedTypeConfig) {
          return {
            isValid: false,
            error: ConnectionValidationError.FILTERED_CONNECTION,
            suggestion: `${targetNodeType.description.displayName} does not accept connections from ${sourceNodeType.description.displayName}`,
          };
        }

        // Validate per-node-type maxConnections
        const existingConnectionsFromType = edges.filter(
          edge =>
            edge.target === targetNode.id &&
            nodes.find(n => n.id === edge.source)?.type === sourceNode.type
        ).length;

        if (existingConnectionsFromType >= allowedTypeConfig.maxConnections) {
          return {
            isValid: false,
            error: ConnectionValidationError.MAX_CONNECTIONS_EXCEEDED,
            suggestion: `${targetNodeType.description.displayName} can only accept ${allowedTypeConfig.maxConnections} connection(s) from ${sourceNodeType.description.displayName}`,
          };
        }
      }

      // Validate source output filter (allowed target types)
      const sourceOutput = getOutputConfiguration(sourceNodeType.description);
      if (sourceOutput?.filter?.allowedNodeTypes) {
        const allowedTypeConfig = sourceOutput.filter.allowedNodeTypes.find(
          config => config.type === targetNode.type
        );

        if (!allowedTypeConfig) {
          return {
            isValid: false,
            error: ConnectionValidationError.FILTERED_CONNECTION,
            suggestion: `${sourceNodeType.description.displayName} can only connect to specific node types`,
          };
        }

        // Validate per-node-type maxConnections for output
        const existingConnectionsToType = edges.filter(
          edge =>
            edge.source === sourceNode.id &&
            nodes.find(n => n.id === edge.target)?.type === targetNode.type
        ).length;

        if (existingConnectionsToType >= allowedTypeConfig.maxConnections) {
          return {
            isValid: false,
            error: ConnectionValidationError.MAX_CONNECTIONS_EXCEEDED,
            suggestion: `${sourceNodeType.description.displayName} can only have ${allowedTypeConfig.maxConnections} connection(s) to ${targetNodeType.description.displayName}`,
          };
        }
      }

      // Validate that source has output and target has input
      const sourceOutputConfig = getOutputConfiguration(sourceNodeType.description);
      const targetInputConfig = getInputConfiguration(targetNodeType.description);

      if (!sourceOutputConfig || !targetInputConfig) {
        return {
          isValid: false,
          error: ConnectionValidationError.INCOMPATIBLE_TYPES,
          suggestion: 'Check that both nodes have compatible input/output configurations',
        };
      }

      // Validate connection counts (max connections per handle)
      const connectionCountValidation = validateConnectionCounts(
        sourceNode,
        targetNode,
        sourceNodeType.description,
        targetNodeType.description
      );

      if (!connectionCountValidation.isValid) {
        return connectionCountValidation;
      }

      return { isValid: true };
    },
    [
      getNodeTypeByName,
      getInputConfiguration,
      getOutputConfiguration,
      validateConnectionCounts,
      nodes,
      edges,
    ]
  );

  return {
    validateConnection,
  };
}
